import React from "react";

import { Box, useMantineTheme } from "@mantine/core";

import { Decimal } from "prisma/cm/client/runtime/library";

const borderRadius = "4px";

interface RangeBarProps {
  lowPercent: number;
  highPercent: number;
}

interface EmptyBarProps {
  empty: true;
}

type Props = RangeBarProps | EmptyBarProps;

export const RANGE_BAR_TRANSITION_DURATION = "0.3s";
export const RANGE_BAR_WIDTH_TRANSITION = `width ${RANGE_BAR_TRANSITION_DURATION} ease-in-out`;
export const RANGE_BAR_POSITION_TRANSITION = `left ${RANGE_BAR_TRANSITION_DURATION} ease-in-out`;
export const RANGE_BAR_HEIGHT = 10;

export function RangeBar(props: Props): JSX.Element {
  const theme = useMantineTheme();
  const bgColor = theme.colors.gray[2];
  const boxShadow = "0px 1px 3px rgba(0, 0, 0, 0.04), 0px 1px 2px rgba(0, 0, 0, 0.08)";

  if ("empty" in props) {
    return (
      <Box
        w="100%"
        h={RANGE_BAR_HEIGHT}
        sx={{
          display: "flex",
          background: bgColor,
          borderRadius,
        }}
      />
    );
  }

  const { lowPercent, highPercent } = props;

  if (lowPercent > highPercent) {
    console.warn("lowPercent cannot be greater than highPercent");
  }

  const singlePoint = lowPercent === highPercent;

  function renderRange() {
    let width = highPercent - lowPercent;
    if (width % 2 !== 0) {
      width = Math.round(width / 2) * 2;
    }
    return (
      <Box
        bg="brand.5"
        sx={{
          boxShadow,
          position: "absolute",
          left: `${lowPercent}%`,
          width: `${width}%`,
          height: "100%",
          transition: `${RANGE_BAR_WIDTH_TRANSITION}, ${RANGE_BAR_POSITION_TRANSITION}`,
          borderRadius,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          h={RANGE_BAR_HEIGHT}
          w={RANGE_BAR_HEIGHT}
          sx={{
            borderRadius: "50%",
            // position: "absolute",
            // left: `calc(50% - ${RANGE_BAR_HEIGHT / 2}px)`,
            backgroundColor: bgColor,
          }}
        />
      </Box>
    );
  }

  function renderPoint() {
    return (
      <Box
        sx={{
          borderRadius: "50%",
          aspectRatio: "1 / 1",
          height: RANGE_BAR_HEIGHT,
          position: "relative",
          top: -1,
          left: `${Math.min(98.5, lowPercent)}%`,
          border: `1px solid var(--mantine-color-brand-5)`,
          background: "rgba(40, 183, 89, 0.1)",
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04), 0px 1px 2px rgba(0, 0, 0, 0.08);",
        }}
      />
    );
  }

  if (!singlePoint) {
    if (lowPercent >= 100 && highPercent > 100) {
      return <RangeBar lowPercent={95} highPercent={100} />;
    } else if (highPercent > 100) {
      return <RangeBar lowPercent={lowPercent} highPercent={100} />;
    }
  }

  return (
    <Box
      sx={{ display: "flex", borderRadius, background: bgColor, position: "relative" }}
      h={RANGE_BAR_HEIGHT}
      w="100%"
    >
      {singlePoint ? renderPoint() : renderRange()}
    </Box>
  );
}

export type RangeBarWithFixedSizeProps = {
  lowValue: number | Decimal;
  highValue: number | Decimal;
};

export const FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY = 20_000;
export const FIXED_SIZE_RANGE_BAR_HIGH_BOUNDARY = 500_000;

export function RangeBarWithFixedSize({
  lowValue,
  highValue,
}: RangeBarWithFixedSizeProps): JSX.Element {
  const lowPercent =
    ((Number(lowValue) - FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY) /
      (FIXED_SIZE_RANGE_BAR_HIGH_BOUNDARY - FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY)) *
    100;
  const highPercent =
    ((Number(highValue) - FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY) /
      (FIXED_SIZE_RANGE_BAR_HIGH_BOUNDARY - FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY)) *
    100;

  return <RangeBar lowPercent={lowPercent} highPercent={highPercent} />;
}
